import type { DetachFragmentOptions, DetachMode } from './index';

/**
 * The single-surface window contract (架构方案 §5.8): a detached window loads the
 * same renderer entry with `?surface=<viewId>&module=<moduleId>` plus the
 * fragment/instance/mode/payload/chrome that describe *what* to render.
 *
 * Writer and reader live in one file on purpose. They used to sit apart — the
 * query was assembled in the Electron main process and parsed in the renderer —
 * which is exactly the shape that drifts once a second runtime needs to assemble
 * the same URL. A round-trip test now pins them together.
 */

/** A single-surface window target parsed from a renderer URL (架构方案 §5.8). */
export interface SurfaceTarget {
  viewId: string;
  moduleId: string;
  /**
   * The detached fragment id (架构方案 §5.8 通用 Detachable). Absent = the whole
   * view is detached (backward-compatible special case).
   */
  fragmentId?: string;
  /** Instance discriminator for a per-instance fragment window (e.g. `c1`). */
  instanceKey?: string;
  /** Origin behaviour of this detached surface; defaults to `migrate`. */
  mode?: DetachMode;
  /** Opaque, module-defined identity for what to render (JSON-decoded from query). */
  payload?: unknown;
  /**
   * Whether to render the detached window's standard chrome (CustomHeader +
   * attach-back). `false` = bare mount (view paints its own drag/controls).
   * Absent = chrome on (default).
   */
  chrome?: boolean;
}

/**
 * Build the query a detached window boots with. Only the fields that describe
 * *what to render* go in the URL; window geometry (`width`/`alwaysOnTop`/…) is a
 * window-driver concern and never round-trips through the page.
 */
export function buildSurfaceQuery(
  options: DetachFragmentOptions,
): Record<string, string> {
  const query: Record<string, string> = {
    surface: options.viewId,
    module: options.moduleId,
    mode: options.mode ?? 'migrate',
  };
  if (options.fragmentId) {
    query.fragment = options.fragmentId;
  }
  if (options.instanceKey) {
    query.instance = options.instanceKey;
  }
  if (options.payload !== undefined) {
    query.payload = JSON.stringify(options.payload);
  }
  if (options.chrome === false) {
    query.chrome = '0';
  }
  return query;
}

/**
 * Parse the `detach` single-surface target from a `location.search` string (or a
 * full URL's query). When `surface` and `module` are present this returns the
 * target so the bootstrap renders only that surface, otherwise `null` (the normal
 * full-workbench window). Pure so it can be unit-tested without a DOM.
 */
export function parseSurfaceTarget(search: string): SurfaceTarget | null {
  const params = new URLSearchParams(search);
  const viewId = params.get('surface');
  const moduleId = params.get('module');
  if (!viewId || !moduleId) {
    return null;
  }
  const fragmentId = params.get('fragment') ?? undefined;
  const instanceKey = params.get('instance') ?? undefined;
  const modeRaw = params.get('mode');
  const mode: DetachMode | undefined =
    modeRaw === 'migrate' || modeRaw === 'mirror' ? modeRaw : undefined;
  const payloadRaw = params.get('payload');
  let payload: unknown;
  if (payloadRaw !== null) {
    try {
      payload = JSON.parse(payloadRaw);
    } catch {
      payload = undefined;
    }
  }
  const chromeRaw = params.get('chrome');
  const chrome = chromeRaw === '0' || chromeRaw === 'false' ? false : undefined;
  return {
    viewId,
    moduleId,
    ...(fragmentId ? { fragmentId } : {}),
    ...(instanceKey ? { instanceKey } : {}),
    ...(mode ? { mode } : {}),
    ...(payload !== undefined ? { payload } : {}),
    ...(chrome === false ? { chrome: false } : {}),
  };
}

/**
 * Parse a surface target out of a **full URL** (the web host only ever sees a
 * page's `href`, never its `search` in isolation). Malformed URLs yield `null`
 * rather than throwing — an unparsable href is simply not a detached window.
 */
export function parseSurfaceTargetFromHref(href: string): SurfaceTarget | null {
  try {
    return parseSurfaceTarget(new URL(href).search);
  } catch {
    return null;
  }
}
